import { useRef } from 'react';
import { toast } from 'sonner';

import {
  AegisManagementStatus,
  AegisManagementTask,
  ApiError,
} from '@/hooks/useAegisManagement';
import { useGetAgents } from '@/hooks/useAgents';
import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { POLLING_INTERVALS, STALE_TIMES } from '@/sections/agents/constants';
import type { UseTaskMonitoringReturn } from '@/sections/agents/types/hooks.types';
import { useMutation, useQuery } from '@/utils/api';

/**
 * Domain-specific hook for monitoring Aegis management tasks
 *
 * Provides focused functionality for task monitoring, cancellation, and status tracking
 * with adaptive polling based on task activity.
 *
 * @param agentId - Agent client ID to filter tasks for specific agent
 * @param enabled - Whether the hook should be active (useful for modal state)
 */
export const useTaskMonitoring = (
  agentId?: string,
  enabled = true
): UseTaskMonitoringReturn => {
  const axios = useAxios();
  const { invalidate: invalidateAgents } = useGetAgents();
  const previousActiveTasksRef = useRef<boolean>(false);

  // Get tasks for the current agent with adaptive polling
  const { data: tasks, isLoading: tasksLoading } = useQuery<
    AegisManagementTask[]
  >({
    defaultErrorMessage: 'Failed to fetch tasks',
    queryKey: getQueryKey.getAegisManagementTasks(agentId),
    queryFn: async (): Promise<AegisManagementTask[]> => {
      if (!agentId) return [];

      try {
        const response = await axios.get<AegisManagementTask[]>(
          '/aegis/management/tasks'
        );
        const allTasks = response.data || [];
        // Filter tasks for this specific agent
        return allTasks.filter(
          (task: AegisManagementTask) => task.aegisClientId === agentId
        );
      } catch (error) {
        console.error('Failed to fetch tasks:', error);
        return [];
      }
    },
    enabled: !!agentId && enabled,
    refetchInterval: query => {
      // Adaptive polling: only poll when enabled and if there are active tasks
      if (!enabled) return false;
      const data = query.state.data;
      if (!Array.isArray(data)) return false;

      const hasActiveTasks = data.some(
        (task: AegisManagementTask) =>
          task.status === AegisManagementStatus.PENDING ||
          task.status === AegisManagementStatus.RUNNING
      );

      // If we had active tasks previously but now we don't, invalidate agents
      if (previousActiveTasksRef.current && !hasActiveTasks) {
        toast.success('Task completion detected');
        invalidateAgents();
      }
      previousActiveTasksRef.current = hasActiveTasks;

      // Poll at configured interval when tasks are active
      return hasActiveTasks ? POLLING_INTERVALS.TASKS : false;
    },
    staleTime: STALE_TIMES.TASKS,
    select: (data): AegisManagementTask[] => data ?? [],
  });

  // Cancel task mutation
  const cancelTaskMutation = useMutation({
    defaultErrorMessage: 'Failed to cancel task',
    mutationFn: async (task: { key: string }) => {
      const response = await axios.delete('/aegis/management/tasks', {
        data: task,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getAegisManagementTasks(),
      });
      toast.success('Task cancellation requested');
    },
    onError: (error: ApiError) => {
      let message: string;
      switch (error.type) {
        case 'network':
          message =
            error.message || 'Network error occurred while canceling task';
          break;
        case 'validation':
          message =
            error.response.data.message ||
            'Invalid task key provided for cancellation';
          break;
        case 'server':
          message =
            error.response.data.message ||
            'Server error occurred while canceling task';
          break;
        case 'unknown':
        default:
          message =
            error.response?.data?.message ||
            error.message ||
            'Failed to cancel task';
          break;
      }
      toast.error(message);
    },
  });

  // Get specific task
  const getTask = async (taskKey: string) => {
    try {
      const response = await axios.get('/aegis/management/tasks', {
        params: { key: taskKey },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get task:', error);
      throw error;
    }
  };

  // Helper functions for task analysis
  const getActiveTasks = (taskList = tasks) =>
    taskList?.filter(
      task =>
        task.status === AegisManagementStatus.PENDING ||
        task.status === AegisManagementStatus.RUNNING
    ) ?? [];

  const getCompletedTasks = (taskList = tasks) =>
    taskList?.filter(task => task.status === AegisManagementStatus.COMPLETED) ??
    [];

  const getFailedTasks = (taskList = tasks) =>
    taskList?.filter(task => task.status === AegisManagementStatus.FAILED) ??
    [];

  const hasActiveTasks = () => getActiveTasks().length > 0;

  // Get most recent task for auto-expansion
  const getMostRecentTask = () => {
    if (!Array.isArray(tasks) || tasks.length === 0) return null;

    const sortedTasks = [...tasks].sort((a, b) => {
      const aDate = new Date(a.created || 0).getTime();
      const bDate = new Date(b.created || 0).getTime();
      return bDate - aDate;
    });

    return sortedTasks[0] ?? null;
  };

  return {
    tasks: tasks ?? [],
    tasksLoading,
    cancelTask: cancelTaskMutation.mutateAsync,
    getTask,
    getActiveTasks,
    getCompletedTasks,
    getFailedTasks,
    hasActiveTasks,
    getMostRecentTask,
    isLoading: tasksLoading || cancelTaskMutation.isPending,
    enabled,
  };
};
